import React, { useState, useRef, useEffect } from "react";
import "../../../css/Common/Components/DesignSystem/NavBar.css";
import { ReactComponent as Logo } from "../../../assets/icons/logo-white.svg";
import { ReactComponent as MenuIcon } from "../../../assets/icons/Hamburger Button.svg";
import ButtonSmall from "./Buttons/ButtonSmall";
import { useNavigate, useLocation } from "react-router-dom";
import { ReactComponent as CloseIcon } from "../../../assets/icons/close.svg";

/**
 * NavBar component renders the main navigation bar with responsive overlays for navigation items.
 * Handles navigation for both small and large screens, displaying overlays for sub-navigation
 * (Advisers and Customers) and managing active states.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {Array<Object>} props.navigationItems - Array of navigation item objects to display in the navbar.
 *   Each item should have the following shape:
 *   {
 *     label: string,         // Display label for the navigation item
 *     navigateTo: string,    // Route path to navigate to
 *     state?: object         // Optional state to pass to the route
 *   }
 *
 * @example
 * const navigationItems = [
 *   { label: "Home", navigateTo: "/" },
 *   { label: "Advisers", navigateTo: "/Adviser" },
 *   { label: "Customers", navigateTo: "/Customers" }
 * ];
 * <NavBar navigationItems={navigationItems} />
 *
 * @returns {JSX.Element} The rendered NavBar component.
 */
const NavBar = ({ navigationItems }) => {
  const navigate = useNavigate();
  const [activeIndex, setActiveIndex] = useState(null);
  const location = useLocation();
  const [isOverlayVisible, setOverlayVisible] = useState(false);
  const [servicesOverlayVisible, setServicesOverlayVisible] = useState(false);
  const [currentServices, setCurrentServices] = useState([]);
  const overlayRef = useRef(null);
  const [subOverlayPosition, setSubOverlayPosition] = useState({
    top: 0,
    left: 0,
  });
  const navItemRefs = useRef([]);

  const adviserServices = [
    { name: "Personal Approach", route: "/Adviser/PersonalApproach" },
    { name: "Support", route: "/Adviser/Support" },
    { name: "Technology", route: "/Adviser/Technology" },
  ];

  const customerServices = [
    { name: "Mortgage Planning", route: "/OurServices/MortgagePlanning" },
    { name: "Investment Planning", route: "/OurServices/InvestmentPlanning" },
    { name: "Equity Release", route: "/OurServices/EquityRelease" },
    { name: "Retirement Planning", route: "/OurServices/RetirementPlanning" },
    { name: "Corporate Planning", route: "/OurServices/CorporatePlanning" },
    { name: "Insurance/Protection", route: "/OurServices/InsuranceProtection" },
  ];

  const isSmallScreen = window.innerWidth < 839; // Example breakpoint

  /**
   * Toggles the visibility of the overlay menu.
   * When called, this function inverts the current state of `isOverlayVisible`
   * by calling `setOverlayVisible` with the opposite value.
   *
   * @function
   * @returns {void}
   */
  const handleMenuClick = () => {
    setOverlayVisible(!isOverlayVisible);
  };

  /**
   * Handles click events on navigation items.
   *
   * - On small screens: Navigates directly to the item's route and closes overlays.
   * - On larger screens:
   *    - If the item is "Advisers" or "Customers", displays a sub-overlay with relevant services.
   *    - Otherwise, navigates directly to the item's route and closes the services overlay.
   *
   * @param {Object} item - The navigation item that was clicked.
   * @param {string} item.label - The display label of the navigation item.
   * @param {string} item.navigateTo - The route path to navigate to.
   * @param {Object} [item.state] - Optional state to pass to the navigation.
   * @param {number} index - The index of the clicked navigation item.
   */
  const handleItemClick = (item, index) => {
    setActiveIndex(index);

    // If small screen, navigate directly on all items
    if (isSmallScreen) {
      navigate(item.navigateTo, { state: item.state });
      setServicesOverlayVisible(false);
      setOverlayVisible(false);
      return;
    }

    // Larger screens: Show sub-overlay for Advisers/Customers, else navigate
    if (item.label === "Advisers" || item.label === "Customers") {
      const navItemRect = navItemRefs.current[index].getBoundingClientRect();
      setSubOverlayPosition({
        top: navItemRect.bottom,
        left: navItemRect.left,
      });

      if (item.label === "Advisers") {
        setCurrentServices(adviserServices);
      } else {
        setCurrentServices(customerServices);
      }
      setServicesOverlayVisible(true);
    } else {
      navigate(item.navigateTo, { state: item.state });
      setServicesOverlayVisible(false);
    }
  };

  /**
   * Handles the close button click event for the overlay.
   * Sets the overlay visibility state to false, effectively hiding the overlay.
   *
   * @function
   * @returns {void}
   */
  const handleCloseClick = () => {
    setOverlayVisible(false);
  };

  /**
   * Handles the click event for a service navigation item.
   * Navigates to the specified route and hides the services overlay.
   *
   * @param {string} route - The route to navigate to when a service is clicked.
   */
  const handleServiceClick = (route) => {
    navigate(route);
    setServicesOverlayVisible(false);
  };

  useEffect(() => {
    /**
     * Handles clicks outside the overlay element to close any visible overlays.
     *
     * This function checks if either the main overlay or the services overlay is visible.
     * If a click event occurs outside the overlay referenced by `overlayRef`, it will
     * hide both overlays by setting their visibility state to false.
     *
     * @param {MouseEvent} event - The mouse event triggered by a click.
     */
    const handleClickOutside = (event) => {
      if (
        (isOverlayVisible || servicesOverlayVisible) &&
        overlayRef.current &&
        !overlayRef.current.contains(event.target)
      ) {
        setOverlayVisible(false);
        setServicesOverlayVisible(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOverlayVisible, servicesOverlayVisible]);

  return (
    <div className="Nav">
      <div className="Nav-Menu" onClick={handleMenuClick}>
        <MenuIcon />
      </div>

      {isOverlayVisible && (
        <div className="NavBar-Overlay">
          <div className="Close-Button">
            <CloseIcon onClick={handleCloseClick} />
          </div>
          <div className="NavBar-Overlay-Content" ref={overlayRef}>
            <ul>
              {navigationItems.map((item, index) => (
                <li
                  key={index}
                  className={`nav-item ${
                    location.pathname === item.navigateTo ? "active" : ""
                  }`}
                  onClick={() => handleItemClick(item, index)}
                  ref={(el) => (navItemRefs.current[index] = el)}
                >
                  {item.label}
                </li>
              ))}
              <li
                onClick={() => {
                  window.open(
                    "https://www.adviser.rosemountfs-ifa.co.uk/",
                    "_blank"
                  );
                }}
              >
                Log In
              </li>
            </ul>
          </div>
        </div>
      )}

      {servicesOverlayVisible && !isSmallScreen && (
        <div
          className="NavBar-Sub-Overlay"
          style={{ top: subOverlayPosition.top, left: subOverlayPosition.left }}

        >
          <div className="NavBar-Sub-Overlay-Content" ref={overlayRef}>
            <ul>
              {currentServices.map((service, idx) => (
                <li
                  key={idx}
                  className="nav-item"
                  onClick={() => handleServiceClick(service.route)}
                >
                  {service.name}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <div
        className="Nav-Logo"
        onClick={() => {
          navigate("/");
        }}
      >
        <Logo />
      </div>

      <ul className="nav-list">
        {navigationItems.map((item, index) => (
          <li
            key={index}
            className={`nav-item ${
              location.pathname === item.navigateTo ? "active" : ""
            }`}
            onClick={() => handleItemClick(item, index)}
            ref={(el) => (navItemRefs.current[index] = el)}
          >
            {item.label}
          </li>
        ))}
      </ul>

      <ButtonSmall
        buttonText="Log In"
        onClick={() => {
          window.open("https://www.adviser.rosemountfs-ifa.co.uk/", "_blank");
        }}
      />
    </div>
  );
};

export default NavBar;
